// src/components/forms/shared/BookingCardTotals.tsx

import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import SummaryRow from './SummaryRow';

interface BookingPetResponse {
  id: number;
  petId: number;
  petName: string;
  petType: string;
  petBreed: string;
  petSize: string;
  useSharingRate: boolean;
  hasHeater: boolean;
  dailyRate: number;
  numberOfNights: number;
  accommodationTotal: number;
  groomingTotal: number;
  petTotal: number;
  lineItemDiscount?: number;
  lineItemDiscountPercentage?: number;
  rateType: string | null;
}

interface BookingKennelResponse {
  id: number;
  kennelNumber: number;
  kennelSide: string;
  kennelSideId: number;
  size?: string;
  heaterNights: number;
  heaterDailyRate: number;
}

interface BookingSurchargeResponse {
  id: number;
  reason: string;
  amount: number;
}

interface BookingCardFinancialsProps {
  pets: BookingPetResponse[];
  kennels?: BookingKennelResponse[];
  surcharges?: BookingSurchargeResponse[];
  depositAmount?: number;
  isQuotation?: boolean;
}

const VAT_RATE = 0.15;

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const BookingCardFinancials: React.FC<BookingCardFinancialsProps> = ({
  pets,
  kennels = [],
  surcharges = [],
  depositAmount = 0,
  isQuotation = false,
}) => {
  const theme = useTheme();

  const getRowBgColor = (rowIdx: number) =>
    rowIdx % 2 === 0
      ? 'transparent'
      : theme.palette.mode === 'dark'
        ? 'rgba(33, 150, 243, 0.08)'
        : 'rgba(0, 0, 0, 0.1)';

  const totals = useMemo(() => {
    const petSubtotal = pets.reduce((sum, pet) => sum + pet.petTotal, 0);

    const kennelSubtotal = kennels.reduce((sum, kennel) => {
      return sum + kennel.heaterDailyRate * kennel.heaterNights;
    }, 0);

    const surchargeSubtotal = surcharges.reduce(
      (sum, surcharge) => sum + surcharge.amount,
      0
    );

    const totalLineItemDiscounts = pets.reduce(
      (sum, pet) => sum + (pet.lineItemDiscount || 0),
      0
    );

    const subtotal = petSubtotal + kennelSubtotal + surchargeSubtotal;
    const vat = Math.round((subtotal - subtotal / 1.15) * 100) / 100;
    const total = subtotal;

    return {
      petSubtotal,
      totalLineItemDiscounts,
      kennelSubtotal,
      surchargeSubtotal,
      subtotal,
      vat,
      total,
    };
  }, [pets, kennels, surcharges]);

  const subRows: Array<{
    label: string | React.ReactNode;
    value?: string | React.ReactNode;
    backgroundColor?: string;
    rowType?: 'default' | 'total';
    borderBottom?: string;
  }> = [];

  let rowIdx = 0;

  if (totals.petSubtotal > 0) {
    subRows.push({
      label: 'Pets Subtotal',
      value: formatCurrency(totals.petSubtotal),
      backgroundColor: getRowBgColor(rowIdx++),
    });
  }

  subRows.push({
    label: 'Discount',
    value:
      totals.totalLineItemDiscounts > 0
        ? `-${formatCurrency(totals.totalLineItemDiscounts)}`
        : formatCurrency(0),
    backgroundColor: getRowBgColor(rowIdx++),
  });

  if (totals.kennelSubtotal > 0) {
    subRows.push({
      label: isQuotation ? 'Heaters Subtotal' : 'Kennels Subtotal',
      value: formatCurrency(totals.kennelSubtotal),
      backgroundColor: getRowBgColor(rowIdx++),
    });
  }

  if (totals.surchargeSubtotal > 0) {
    subRows.push({
      label: 'Surcharges Subtotal',
      value: formatCurrency(totals.surchargeSubtotal),
      backgroundColor: getRowBgColor(rowIdx++),
    });
  }

  subRows.push({
    label: '',
    value: '',
    backgroundColor: 'transparent',
  });

  subRows.push({
    label: 'Subtotal',
    value: formatCurrency(totals.subtotal),
    backgroundColor: getRowBgColor(rowIdx++),
  });

  subRows.push({
    label: `VAT incl. (${(VAT_RATE * 100).toFixed(0)}%)`,
    value: formatCurrency(totals.vat),
    backgroundColor: getRowBgColor(rowIdx++),
  });

  subRows.push({
    label: '',
    value: '',
    backgroundColor: 'transparent',
  });

  subRows.push({
    label: 'TOTAL',
    value: formatCurrency(totals.total),
    rowType: 'total',
  });

  if (!isQuotation) {
    subRows.push({
      label: 'Deposit Paid',
      value:
        depositAmount > 0
          ? `-${formatCurrency(depositAmount)}`
          : formatCurrency(0),
      backgroundColor: getRowBgColor(rowIdx++),
      borderBottom: 'none',
    });
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '800px' }}>
      <Box
        sx={{
          marginBottom: 0,
          '& > div': {
            borderLeft: 'none',
            pl: 0,
            borderRadius: 0,
            backgroundColor: 'transparent',
            '& > div > div': {
              pr: '1rem',
              pl: '1rem',
            },
            '@media (max-width: 600px)': {
              pl: 0,
              '& > div > div': {
                pr: 1.5,
                pl: 1.5,
              },
            },
          },
        }}
      >
        <SummaryRow
          subRowBorderBottom={`2px dashed ${theme.palette.dividerContrast}`}
          label=""
          value=""
          subRows={subRows}
          subRowLabelColor="text.primary"
        />
      </Box>
    </Box>
  );
};

export default BookingCardFinancials;
